import { APIClientServices } from "./APIClientServices";

export type ScheduledTaskRecurrenceType = "once" | "daily" | "weekly" | "monthly" | "cron";
export type ScheduledTaskStatus = "active" | "paused" | "archived";
export type ScheduledTaskRunStatus = "running" | "success" | "failed" | "partial";

export interface ScheduledTaskSubtaskPayload {
	team_member_id: string;
	title?: string;
	objective: string;
	description?: string;
}

export interface ScheduledTaskItemPayload {
	title: string;
	objective: string;
	description?: string;
	subtasks: ScheduledTaskSubtaskPayload[];
}

export interface ScheduledTaskPayload {
	objective: string;
	system_prompt: string;
	items: ScheduledTaskItemPayload[];
}

export interface ScheduledTaskRecurrenceConfig {
	time?: string;
	days_of_week?: number[];
	day_of_month?: number;
	run_at?: string;
	cron_expression?: string;
}

export interface PublicScheduledTaskDTO {
	id: string;
	workspaceId: string;
	teamId: string;
	accountId: string;
	title: string;
	description: string | null;
	payload: ScheduledTaskPayload;
	recurrenceType: ScheduledTaskRecurrenceType;
	recurrenceConfig: ScheduledTaskRecurrenceConfig;
	cronExpression: string | null;
	timezone: string;
	status: ScheduledTaskStatus;
	nextRunAt: string | null;
	lastRunAt: string | null;
	lastStatus: ScheduledTaskRunStatus | null;
	runsTotal: number;
	runsSucceeded: number;
	runsFailed: number;
	maxRuns: number | null;
	createdAt: string;
	updatedAt: string;
}

export interface PublicTaskExecutionLogDTO {
	id: string;
	scheduledTaskId: string;
	taskId: string | null;
	runNumber: number;
	status: ScheduledTaskRunStatus;
	startedAt: string;
	finishedAt: string | null;
	tokensUsed: number;
	failureReason: string | null;
	errorDetails: Record<string, any> | null;
	createdAt: string;
	updatedAt: string;
}

export interface CreateScheduledTaskInput {
	team_id: string;
	title: string;
	description?: string | null;
	payload: ScheduledTaskPayload;
	recurrence_type: ScheduledTaskRecurrenceType;
	recurrence_config: ScheduledTaskRecurrenceConfig;
	timezone?: string;
	max_runs?: number | null;
}

export interface UpdateScheduledTaskInput {
	title?: string;
	description?: string | null;
	payload?: ScheduledTaskPayload;
	recurrence_type?: ScheduledTaskRecurrenceType;
	recurrence_config?: ScheduledTaskRecurrenceConfig;
	timezone?: string;
	max_runs?: number | null;
}

export class ScheduledTaskServices {
	private static base(workspaceId: string): string {
		return `v1/workspaces/${workspaceId}/scheduled-tasks`;
	}

	static async list(workspaceId: string, filters?: { status?: ScheduledTaskStatus }): Promise<PublicScheduledTaskDTO[]> {
		const qs = filters?.status ? `?status=${filters.status}` : "";
		return APIClientServices.get<PublicScheduledTaskDTO[]>(`${this.base(workspaceId)}${qs}`);
	}

	static async create(workspaceId: string, input: CreateScheduledTaskInput): Promise<PublicScheduledTaskDTO> {
		return APIClientServices.post<PublicScheduledTaskDTO>(this.base(workspaceId), input);
	}

	static async get(workspaceId: string, scheduledId: string): Promise<PublicScheduledTaskDTO> {
		return APIClientServices.get<PublicScheduledTaskDTO>(`${this.base(workspaceId)}/${scheduledId}`);
	}

	static async update(
		workspaceId: string,
		scheduledId: string,
		patch: UpdateScheduledTaskInput,
	): Promise<PublicScheduledTaskDTO> {
		return APIClientServices.put<PublicScheduledTaskDTO>(`${this.base(workspaceId)}/${scheduledId}`, patch);
	}

	static async cancel(workspaceId: string, scheduledId: string): Promise<void> {
		return APIClientServices.delete(`${this.base(workspaceId)}/${scheduledId}`);
	}

	static async pause(workspaceId: string, scheduledId: string): Promise<PublicScheduledTaskDTO> {
		return APIClientServices.post<PublicScheduledTaskDTO>(`${this.base(workspaceId)}/${scheduledId}/pause`, {});
	}

	static async resume(workspaceId: string, scheduledId: string): Promise<PublicScheduledTaskDTO> {
		return APIClientServices.post<PublicScheduledTaskDTO>(`${this.base(workspaceId)}/${scheduledId}/resume`, {});
	}

	static async listLogs(
		workspaceId: string,
		scheduledId: string,
		limit: number = 50,
	): Promise<PublicTaskExecutionLogDTO[]> {
		return APIClientServices.get<PublicTaskExecutionLogDTO[]>(
			`${this.base(workspaceId)}/${scheduledId}/logs?limit=${limit}`,
		);
	}
}
